import { Children, PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import * as Accordion from "@radix-ui/react-accordion";

import WaterfallRowLabel from "~/components/build/Show/components/Waterfall/WaterfallRowLabel";
import WaterfallRowChart from "~/components/build/Show/components/Waterfall/WaterfallRowChart";

import Icon from "~/components/shared/Icon";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { WaterfallRowType } from "~/components/build/Show/lib/queries/waterfall";

interface AccordionProps {
  depth: number;
  id: string;
  row: WaterfallRowType;
}

const AccordionWrapper = ({ children, depth, id, row }: PropsWithChildren<AccordionProps>) => {
  const [first, ...rest] = Children.toArray(children);
  const { currentStep, currentJob } = useCurrentJobOrStep();

  const isChildFailed = useMemo(() => {
    if (row.children.some((child) => !child.bars.some((bar) => bar.passed))) {
      return true;
    }
  }, [row.step_uuid]);

  const isChildActive = useMemo(() => {
    const job = currentJob;
    const stepUuid = currentStep?.uuid;

    if (row.type === "group" || row.children.some((child) => child.step_uuid === stepUuid)) {
      const childStepIds = row.children?.map((step) => step.step_uuid) || [];

      // step within group step is clicked
      if (stepUuid && childStepIds.includes(stepUuid)) {
        return true;
      }

      // job within group step is clicked
      if (job && job.groupUuid === row.step_uuid) {
        return true;
      }
    }

    if (job && row.step_uuid === job.stepUuid) {
      return true;
    }
  }, [currentJob?.id, currentStep?.uuid]);

  // start open if a child is active, failed or it's a group step
  const [value, setValue] = useState<string[]>(
    isChildActive || isChildFailed || row.type === "group" ? [row.step_uuid] : [],
  );

  useEffect(() => {
    currentStep && setValue((prev) => [...prev, currentStep?.uuid]);
  }, [currentJob?.id, currentStep?.uuid]);

  useEffect(() => {
    if (isChildActive) {
      setValue([row.step_uuid]);
    }
  }, [currentJob?.id, currentStep?.uuid]);

  useEffect(() => {
    if (isChildFailed) {
      setValue([row.step_uuid]);
    }
  }, [isChildFailed]);

  const onValueChange = useCallback((value: string[]) => {
    setValue(value);
  }, []);

  const open = useCallback(() => {
    setValue([row.step_uuid]);
  }, []);

  return (
    <Accordion.Root
      type="multiple"
      className="flex w-full min-w-0 flex-auto"
      onValueChange={onValueChange}
      value={value}
    >
      <Accordion.Item value={id} className="w-full">
        <div>
          <Accordion.Trigger
            tabIndex={-1}
            className="group relative w-full text-left [&>div>svg]:data-[state=open]:rotate-90"
            onClick={open}
          >
            {first}
            <div
              tabIndex={-1}
              className="absolute bottom-[2px] left-[2px] top-[2px] flex h-6 w-6 items-center justify-center self-center rounded-sm hover:hover:bg-[#CDC6FF]"
              style={{ marginLeft: `calc(${depth}rem + ${depth * 4}px)` }}
            >
              <Icon
                icon="heroicons/outline/chevron-right"
                className="h-3 w-3 transition-transform duration-150"
              />
            </div>
          </Accordion.Trigger>
        </div>
        <div className="overflow-y-clip">
          <Accordion.Content className="relative data-[state=closed]:animate-[radixAccordionSlideUp_150ms_ease-in-out] data-[state=open]:animate-[radixAccordionSlideDown_150ms_ease-in-out]">
            {rest}
          </Accordion.Content>
        </div>
      </Accordion.Item>
    </Accordion.Root>
  );
};

const MaybeAccordion = ({
  children,
  isAccordion,
  ...accordianProps
}: PropsWithChildren<AccordionProps & { isAccordion: boolean }>) => {
  if (!children) {
    return null;
  }

  if (isAccordion) {
    return <AccordionWrapper {...accordianProps}>{children}</AccordionWrapper>;
  }
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};

interface WaterfallRowProps {
  uniqueKey: (string | undefined)[];
  row: WaterfallRowType;
  barContainerPadding: number;
  depth: number;
  allowCollapse: boolean;
}

const WaterfallRow = ({
  uniqueKey,
  row,
  barContainerPadding,
  depth,
  allowCollapse,
}: WaterfallRowProps) => {
  const parentOfGroup =
    allowCollapse &&
    (row.type === "group" || row.children.some((child) => !!child.parallel_group_index));

  return (
    <MaybeAccordion isAccordion={parentOfGroup} depth={depth} id={row.step_uuid} row={row}>
      <div className="mt-0 grid w-full grid-flow-dense grid-cols-4 divide-x divide-gray-400 pt-0">
        <WaterfallRowLabel
          key={["label", ...uniqueKey].join?.("-")}
          data={row}
          depth={depth}
          collapsable={parentOfGroup}
        />
        <WaterfallRowChart
          key={["chart", ...uniqueKey].join("-")}
          barContainerPadding={barContainerPadding}
          data={row}
          depth={depth}
        />
      </div>
      {row.children.length > 0 &&
        row.children.map((childData) => {
          const uniqueKey = [childData.step_uuid, childData.job_uuid];
          return (
            <WaterfallRow
              uniqueKey={[childData.step_uuid, childData.job_uuid]}
              key={uniqueKey.join("-")}
              row={childData}
              barContainerPadding={barContainerPadding}
              depth={depth + 1}
              allowCollapse={allowCollapse}
            />
          );
        })}
    </MaybeAccordion>
  );
};

export default WaterfallRow;
